import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { adminApi } from '../../api';
import type { ListBoardsResult, BoardStatus } from '../../types';
import { isAdmin } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';
import { AdminBreadcrumbs } from '../../components/admin';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatusChip({ status }: { status: BoardStatus }) {
  const colors: Record<BoardStatus, 'default' | 'success' | 'warning' | 'info'> = {
    draft: 'default',
    live: 'success',
    locked: 'warning',
    completed: 'info',
  };

  const labels: Record<BoardStatus, string> = {
    draft: 'Draft',
    live: 'Live',
    locked: 'Locked',
    completed: 'Completed',
  };

  return <Chip label={labels[status]} color={colors[status]} size="small" />;
}

export function BoardsManagementPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  useDocumentTitle('Boards Management');

  const [result, setResult] = useState<ListBoardsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<BoardStatus | ''>(
    (searchParams.get('status') as BoardStatus) || ''
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => getStoredRowsPerPage());

  const fetchBoards = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.listAllBoards({
        search: search || undefined,
        status: statusFilter || undefined,
        includeDisabled: true,
        page: page + 1,
        pageSize,
      });
      setResult(data);
    } catch (err) {
      setError('Failed to load boards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page, pageSize]);

  useEffect(() => {
    if (!isAdmin(currentUser)) {
      navigate('/');
      return;
    }
    fetchBoards();
  }, [currentUser, navigate, fetchBoards]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleStatusFilterChange = (value: BoardStatus | '') => {
    setStatusFilter(value);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('status', value);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  if (loading && !result) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <AdminBreadcrumbs items={[{ label: 'Boards' }]} />
      <Typography variant="h4" sx={{ mb: 3 }}>
        Board Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search"
              placeholder="Board name or slug..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            />
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => handleStatusFilterChange(e.target.value as BoardStatus | '')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="live">Live</MenuItem>
                <MenuItem value="locked">Locked</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Boards Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Board</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Game Date</TableCell>
                <TableCell align="right">Squares</TableCell>
                <TableCell align="right">Total Pot</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result?.boards.map((board) => (
                <TableRow
                  key={board.id}
                  hover
                  sx={board.ownerDisabled ? { bgcolor: 'action.hover' } : {}}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {board.ownerDisabled && (
                        <Tooltip title="Owner is disabled - board hidden from users">
                          <WarningIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {board.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {board.homeTeam} vs {board.awayTeam}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {board.owner?.displayName || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {board.owner?.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={board.status} />
                  </TableCell>
                  <TableCell>{formatDate(board.gameDate)}</TableCell>
                  <TableCell align="right">
                    {board.squaresClaimed}/100
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(board.totalPot)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Board">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/boards/${board.id}`)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {result && (
          <TablePagination
            component="div"
            count={result.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPageSize(newSize);
              setStoredRowsPerPage(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Card>
    </Box>
  );
}
